import { useState } from 'react';
import { t } from 'i18next';
import { useColorMode } from '@chakra-ui/react';
import { Button } from '@peacecover/component';
import { useBettieSettingInfo } from '../../hooks/useBettieSettingInfo';
import { useCreateBettieChatRoom } from '@pocapan/shared/domains/bettie/ui/hooks/useCreateBettieChatRoom';
import { useUpdateBettieProfile } from '@pocapan/shared/domains/bettie/ui/hooks/useUpdateBettieProfile';
import { useVirtualKeyboardVisible } from '../hooks/useVirtualKeyboardVisible';
import GradientBodyBox from '../../components/body/GradientBodyBox';
import PulsingGradientCircle from './PulsingGradientCircle';
import SpeechBallon from './SpeechBallon';
import type { BettieSettingStep } from '../BettieSettingPage';
import type { components } from '@pocapan/shared/types/schema';
import { attachJosa, parseMemberCalledMessage } from '@pocapan/shared/utils/bettieUtils';

type ApiV1MemberPersona = components['schemas']['ApiV1MemberPersona'];

interface BettieUserNicknameProps {
  bettiePersona: ApiV1MemberPersona;
  defaultUserNickname: string | null;
  setCurrentStep: (currentStep: BettieSettingStep) => void;
  setChatRoomUuid: (chatRoomUuid: string) => void;
  isForeignUser: boolean;
}

const BettieUserNickname = ({
  bettiePersona,
  defaultUserNickname,
  setCurrentStep,
  setChatRoomUuid,
  isForeignUser
}: BettieUserNicknameProps) => {
  const [isLoading, setIsLoading] = useState(false);

  const { bettieSettingInfo, updateBettieSettingInfo } = useBettieSettingInfo();
  const { colorMode } = useColorMode();
  const createChatRoom = useCreateBettieChatRoom();
  const { mutateAsync: updateBettieProfile } = useUpdateBettieProfile();
  const { isKeyboardVisible, handleFocus, handleBlur } = useVirtualKeyboardVisible();
  const handleCreateChatRoom = async () => {
    if (bettieSettingInfo.userNickname.trim().length === 0) {
      return;
    }
    setIsLoading(true);
    try {
      const requestBody = {
        chat_room: {
          member_persona_id: bettiePersona.memberPersonaId,
          user_nickname: bettieSettingInfo.userNickname.trim(),
          bettie_nickname: bettieSettingInfo.bettieNickname.trim(),
          data_url: bettieSettingInfo.dataUrl || '',
          visibility_picture: bettieSettingInfo.isRecommendProfileImage
        }
      };
      const response = await createChatRoom.mutateAsync(requestBody);
      if (!response.chatRoom) {
        throw new Error('Failed to create chat room');
      }
      setChatRoomUuid(response.chatRoom.chatRoomId);
      setCurrentStep('mood_suggestion');
      const updateRequestBody = {
        chat_room: {
          original_data_url: bettieSettingInfo.originalDataUrl || ''
        }
      };
      await updateBettieProfile({
        body: updateRequestBody,
        chatRoomId: response.chatRoom.chatRoomId
      });
    } catch (error) {
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  };

  const showWarningText =
    bettieSettingInfo.bettieNickname.trim() === bettieSettingInfo.userNickname.trim();

  return (
    <div
      className="min-h-screen flex flex-col overflow-y-auto"
      style={{
        backgroundImage: `url(/assets/bettie/bg_bettie_loading2_${colorMode}.png)`,
        backgroundRepeat: 'round',
        paddingTop: '55px',
        paddingBottom: 'env(safe-area-inset-bottom)'
      }}
    >
      <div className="flex flex-col items-start px-[24px] py-[10px]">
        <h1 className="text-[26px] font-bold text-text-main-lv1 leading-[31px] whitespace-pre-line">
          {t('BettieSetting.bettieUserNickname.title', {
            artist: attachJosa(bettieSettingInfo.bettieNickname, '이/가')
          })}
        </h1>
      </div>
      <div className="flex flex-col items-center mt-[28px]">
        <div className="flex flex-col items-center">
          <div className="mb-[22px] z-10">
            <SpeechBallon
              text={parseMemberCalledMessage(bettiePersona?.nameSelectionMessage, {
                member_name: bettieSettingInfo.bettieNickname
              })}
              description={isForeignUser ? 'What should I call you?' : undefined}
            />
          </div>
          <PulsingGradientCircle
            gradientCircleSize="132px"
            glassCircleSize="128px"
            showDottedCircle={false}
            blur="9.5px"
          >
            <img
              src={bettieSettingInfo.dataUrl as string}
              alt=""
              className="w-[119px] h-[119px] rounded-full object-cover"
            />
          </PulsingGradientCircle>
          <p className="text-[16px] font-semibold text-text-main-lv2 leading-[19px] mt-[4px] z-10">
            {bettieSettingInfo.bettieNickname}
          </p>
        </div>
        <div className="w-full px-[24px] mt-[23px]">
          <GradientBodyBox
            title={t('BettieSetting.bettieUserNickname.inputTitle', {
              artist: attachJosa(bettieSettingInfo.bettieNickname, '이/가')
            })}
            placeholder={defaultUserNickname ?? t('BettieSetting.bettieUserNickname.placeholder')}
            value={bettieSettingInfo.userNickname}
            onFocus={handleFocus}
            onBlur={handleBlur}
            onChange={(e) => updateBettieSettingInfo({ userNickname: e.target.value })}
            className="w-full"
            showWarningText={showWarningText}
            warningText={t('BettieSetting.bettieUserNickname.warningText') as string}
          />
        </div>
      </div>
      <div
        className={`fixed bottom-[calc(10px+env(safe-area-inset-bottom))] left-0 flex flex-col items-center w-full px-[24px] pb-[10px] transition-all duration-150 ease-in-out ${isKeyboardVisible ? 'translate-y-full opacity-0' : 'translate-y-0 opacity-100'}`}
      >
        <Button
          size="large"
          className="w-full text-[17px] font-semibold"
          disabled={!bettieSettingInfo.userNickname || isLoading || showWarningText}
          onClick={handleCreateChatRoom}
        >
          {t('confirm')}
        </Button>
      </div>
    </div>
  );
};

export default BettieUserNickname;
